/*
 * <cart-note-dialog>
 *
 * Opens the cart note in a native <dialog> layered over the cart drawer.
 * showModal() puts it in the browser's top layer, which gives us the focus
 * trap, Escape and background inertness for free — and avoids fighting the
 * theme's global trapFocus(), which tears down any previous trap when called.
 *
 * The element sits outside <cart-drawer>, so it survives every cart re-render.
 * The trigger does not: it lives in the drawer footer and is destroyed on each
 * AJAX swap, which is why it is bound by delegation on document.
 */
if (!customElements.get('cart-note-dialog')) {
  customElements.define(
    'cart-note-dialog',
    class CartNoteDialog extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        this.textarea = this.querySelector('textarea[name="note"]');
        this.saveButton = this.querySelector('[data-cart-note-save]');
        this.trigger = null;

        if (!this.dialog) return;
        if (!this.textarea) return;

        // On /cart with a drawer-type cart, the drawer panel and the page panel
        // both exist. Triggers are matched on aria-controls so each instance
        // only ever answers its own button.
        this.dialogId = this.dialog.id;
        this.triggerSelector = `[data-cart-note-open][aria-controls="${this.dialogId}"]`;

        const closeButton = this.querySelector('[data-cart-note-close]');
        if (closeButton) closeButton.addEventListener('click', () => this.dismiss());

        if (this.saveButton) this.saveButton.addEventListener('click', () => this.save());

        // Route Escape through our own close path so the trigger regains focus.
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.dismiss();
        });

        // Clicking the backdrop resolves to the dialog element itself.
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dismiss();
        });

        document.addEventListener('click', (event) => {
          const trigger = event.target.closest('[data-cart-note-open]');
          if (!trigger) return;
          if (trigger.getAttribute('aria-controls') !== this.dialogId) return;
          event.preventDefault();
          this.trigger = trigger;
          this.show();
        });
      }

      show() {
        if (typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }

        this.textarea.focus();
        const end = this.textarea.value.length;
        this.textarea.setSelectionRange(end, end);
      }

      dismiss() {
        if (typeof this.dialog.close === 'function' && this.dialog.open) {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
        }

        this.syncTrigger();
        if (this.trigger) this.trigger.focus();
      }

      save() {
        // cart.js persists the note on `input` with a 300ms debounce that we
        // cannot flush from outside. Posting again here is idempotent and makes
        // sure keystrokes typed in the last 300ms are never lost.
        if (this.saveButton) {
          this.saveButton.disabled = true;
          this.saveButton.setAttribute('aria-busy', 'true');
        }

        const body = JSON.stringify({ note: this.textarea.value });

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .catch(() => {})
          .finally(() => {
            if (this.saveButton) {
              this.saveButton.disabled = false;
              this.saveButton.removeAttribute('aria-busy');
            }
            this.dismiss();
          });
      }

      syncTrigger() {
        // The footer may have been re-rendered while the panel was open, so
        // re-resolve the trigger instead of trusting the stored reference.
        const trigger = document.querySelector(this.triggerSelector);
        if (!trigger) return;

        const filled = this.textarea.value.trim().length > 0;
        trigger.classList.toggle('is-filled', filled);

        const label = trigger.querySelector('[data-note-label]');
        if (label) {
          label.textContent = filled ? trigger.dataset.labelFilled : trigger.dataset.labelEmpty;
        }

        this.trigger = trigger;
      }
    }
  );
}
